import { useMutation, useQuery } from "@apollo/client";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Refresh from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useTenant } from "hooks/useTenant";
import { useCallback, useState } from "react";

import {
  GENERATE_TASK_CALENDAR_TOKEN_MUTATION,
  REVOKE_TASK_CALENDAR_TOKEN_MUTATION,
  TASK_CALENDAR_SUBSCRIPTION_URL_QUERY,
} from "./schema";

type TaskCalendarSubscriptionDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const TaskCalendarSubscriptionDialog: React.FC<TaskCalendarSubscriptionDialogProps> = ({
  open,
  onClose,
}) => {
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const [copied, setCopied] = useState(false);
  const copyToClipboard = useCopyToClipboard();

  const { data, loading, refetch } = useQuery(TASK_CALENDAR_SUBSCRIPTION_URL_QUERY, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId || !open,
  });

  const [generateToken, { loading: generating }] = useMutation(
    GENERATE_TASK_CALENDAR_TOKEN_MUTATION,
    {
      variables: { tenantId: tenantId ?? "" },
      onCompleted: () => {
        alerts.success("Calendar subscription URL generated");
        refetch();
      },
      onError: (error) => {
        alerts.error(`Failed to generate URL: ${error.message}`);
      },
    }
  );

  const [revokeToken, { loading: revoking }] = useMutation(REVOKE_TASK_CALENDAR_TOKEN_MUTATION, {
    variables: { tenantId: tenantId ?? "" },
    onCompleted: (result) => {
      if (result.revokeTaskCalendarToken) {
        alerts.success("Calendar subscription revoked");
      }
      refetch();
    },
    onError: (error) => {
      alerts.error(`Failed to revoke: ${error.message}`);
    },
  });

  const subscriptionUrl = data?.taskCalendarSubscriptionUrl;

  const handleCopy = useCallback(async () => {
    if (subscriptionUrl) {
      try {
        await copyToClipboard(subscriptionUrl);
        setCopied(true);
        alerts.success("URL copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        alerts.error("Failed to copy URL");
      }
    }
  }, [subscriptionUrl, alerts, copyToClipboard]);

  const handleGenerate = useCallback(() => {
    generateToken();
  }, [generateToken]);

  const handleRevoke = useCallback(() => {
    revokeToken();
  }, [revokeToken]);

  const isLoading = loading || generating || revoking;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      noPrimaryAction
      additionalPrimaryActionElements={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      title={
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarMonth color="primary" />
          <Typography variant="h6">Calendar Subscription</Typography>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <Typography variant="body2" color="text.secondary">
          Subscribe to your tasks in your favorite calendar app (Google Calendar, Apple Calendar,
          Outlook, etc.). Your calendar will automatically sync with tasks you are assigned to or
          watching.
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        ) : subscriptionUrl ? (
          <>
            <TextField
              label="Subscription URL"
              value={subscriptionUrl}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? "Copied!" : "Copy URL"}>
                      <IconButton onClick={handleCopy} edge="end">
                        <ContentCopy color={copied ? "success" : "inherit"} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                },
              }}
            />

            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                <strong>How to use:</strong> Copy this URL and add it as a calendar subscription in
                your calendar app. Changes may take up to 24 hours to appear (depending on your
                calendar app).
              </Typography>
            </Alert>

            <Stack direction="row" spacing={2}>
              <Tooltip title="Generate a new URL (this will invalidate the current one)">
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleGenerate}
                  disabled={isLoading}
                >
                  Regenerate URL
                </Button>
              </Tooltip>
            </Stack>
          </>
        ) : (
          <>
            <Alert severity="warning" variant="outlined">
              <Typography variant="body2">
                You haven't set up a calendar subscription yet. Generate a URL to get started.
              </Typography>
            </Alert>

            <Button
              variant="contained"
              startIcon={<CalendarMonth />}
              onClick={handleGenerate}
              disabled={isLoading}
            >
              Generate Subscription URL
            </Button>
          </>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary">
            <strong>Privacy note:</strong> This URL contains a private token. Anyone with this URL
            can see your tasks. If you believe your URL has been compromised, regenerate it to
            invalidate the old one.
          </Typography>
        </Box>

        {subscriptionUrl && (
          <Box>
            <Link
              component="button"
              variant="body2"
              color="error"
              onClick={handleRevoke}
              sx={{ cursor: "pointer" }}
            >
              Revoke calendar access
            </Link>
          </Box>
        )}
      </Stack>
    </Dialog>
  );
};
